import React from "react";
import { Chart } from "react-chartjs-2";
import "./chartjs.config"; // Import Chart.js registration
import { formatNumber } from "../../../utils/helper";

const CustomTokenChart = ({
  title = "Token used",
  xData = [],
  expectedData = [],
  usedData = [],
  barColor = "#EAB308", // 🟨 Bar color (Expected)
  lineColor = "#06B6D4", // 🟦 Line color (Used)
  placeholderColor = "rgba(209,213,219,0.3)", // Light gray overlay
  showPlaceholder = true,
  labelSuffix = "", // shows "98k"
}) => {
  // ✅ Calculate max value for full-height placeholder
  const maxValue = Math.max(...expectedData, ...usedData) || 100;
  const placeholderHeight = maxValue * 1.2; // 20% larger than max for padding
  
  // ✅ Build datasets
  const datasets = [];

  // Actual expected data bars (add first so they render at bottom of stack)
  datasets.push({
    type: "bar",
    label: "Used",
    data: expectedData,
    backgroundColor: barColor,
    borderRadius: 4,
    barPercentage: 0.6,
    categoryPercentage: 0.6,
    stack: "stack1",
    order: 1, // Render bars first (behind line)
  });

  // Placeholder layer (full-height gray bars on top)
  if (showPlaceholder) {
    const placeholderData = new Array(xData.length).fill(placeholderHeight);
    datasets.push({
      type: "bar",
      label: "Placeholder",
      data: placeholderData,
      backgroundColor: placeholderColor,
      borderWidth: 0,
      barPercentage: 0.6,
      categoryPercentage: 0.6,
      borderRadius: 4,
      stack: "stack1",
      hoverBackgroundColor: placeholderColor, // Keep color on hover
      order: 1, // Render bars first (behind line)
    });
  }

  // Used data line (added last to render on top of bars)
  datasets.push({
    type: "line",
    label: "Used",
    data: usedData,
    borderColor: lineColor,
    backgroundColor: "fff",
    tension: 0, // smooth curves with distinct peaks and dips
    fill: false,
    borderWidth: 2.5,
    pointRadius: 5,
    pointBackgroundColor: lineColor,
    pointBorderColor: "#fff",
    pointBorderWidth: 2,
    pointHoverRadius: 6,
    spanGaps: false, // don't skip gaps in data
    order: 0, // Render line on top (lower order = renders last/on top)
  });

  const chartData = { labels: xData, datasets };

  // ✅ Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 10, left: 5, right: 5, bottom: 0 },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: "#6B7280",
          font: { family: "Plus Jakarta Sans", size: 12 },
        },
        stacked: true,
      },
      y: {
        grid: { 
          display: true,
          color: "#F3F4F6",
        },
        border: { display: false },
        ticks: { display: false },
        stacked: true,
        max: showPlaceholder ? placeholderHeight : undefined, // Full height when placeholder is shown
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: { 
        enabled: true,
        filter: (item) => item.dataset.label !== 'Placeholder', // Hide placeholder from tooltip
      },
    },
    elements: {
      line: { tension: 0.4 }, // smooth curves with peaks and dips
    },
  };

 
  

  // ✅ Custom label plugin (shows values above bars)
  const labelPlugin = {
    id: "labelPlugin",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const barMeta = chart.getDatasetMeta(0); // Expected data is now at index 0
      ctx.save();
      ctx.font = "600 11px 'Plus Jakarta Sans'";
      ctx.textAlign = "center";
      ctx.fillStyle = "#111827";

      barMeta.data.forEach((bar, i) => {
        const value = chart.data.datasets[0].data[i]; // Expected data is at index 0
        if (value != null) {
          const text = `${formatNumber(value)}${labelSuffix}`;
          const textY = Math.max(bar.y - 8, 12);
          ctx.fillText(text, bar.x, textY);
        }
      });
      ctx.restore();
    },
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "10px 10px 20px 10px",
        position: "relative",
        backgroundColor: "white",
      }}
    >
      {/* Placeholder overlay removed - using stacked bars instead */}

      {/* Chart container */}
      <div style={{ flex: 1, position: "relative", zIndex: 1, backgroundColor: "white" }}>
        <Chart
          type="bar"
          data={chartData}
          options={chartOptions}
          plugins={[labelPlugin]}
        />
      </div>

      {/* Legend */}
     
    </div>
  );
};

export default CustomTokenChart;
